import Phaser from 'phaser'
import type { Game } from './Game'
import { useGameStore } from '@/store/gameStore'
import { supabase } from '@/services/supabase'
import { topScores, sinceFor } from '@/services/ranking'

type Player = { id: string; nickname: string; score: number }
type Entry = { rank: number; nickname: string; score: number; isMe?: boolean } | null

export class UI extends Phaser.Scene {
  constructor() { super('UI') }

  create(data: { game: Game }) {
    const { width, height } = this.scale
    const pad = width * 0.04
    const scoreText = this.add
      .text(width / 2, width * 0.04, '0', {
        fontFamily: 'Arial', fontSize: `${Math.round(width * 0.085)}px`, color: '#ffffff',
        fontStyle: 'bold', stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.01),
      })
      .setOrigin(0.5, 0).setShadow(0, 3, '#00000066', 6)
    const stageText = this.add
      .text(pad, width * 0.05, 'STAGE 1', {
        fontFamily: 'Arial', fontSize: `${Math.round(width * 0.05)}px`, color: '#ffffff',
        fontStyle: 'bold', stroke: '#3a2a6a', strokeThickness: Math.round(width * 0.012),
      })
      .setShadow(0, 3, '#00000066', 4)
    const barY = width * 0.2
    const timeBar = this.add.rectangle(width / 2, barY, width * 0.84, width * 0.011, 0x6be36b).setOrigin(0.5)
    this.add.rectangle(width / 2, barY, width * 0.84, width * 0.011, 0xffffff, 0.25).setOrigin(0.5).setDepth(-1)

    // ── 실시간 아레나(C): 지금 플레이 중인 사람들의 라이브 점수로 순위 사다리 ──
    const mode = useGameStore.getState().mode
    const ladder = mode === 'challenge' ? this.buildRankLadder(width, height) : null
    const myNick = (useGameStore.getState().nickname || '나').trim() || '나'
    const myId = `${Date.now().toString(36)}${Math.floor(Math.random() * 1e8).toString(36)}`

    const g = data.game
    let barMax = 60
    let barStage = 1
    let currentScore = 0
    let trackTimer: number | undefined

    // 랭킹 풀 = DB 저장 점수(주간 전체, 최근 7일) + 현재 접속(presence) 라이브 점수 합산
    let dbRows: Array<{ nickname: string; score: number }> = []
    let livePlayers: Player[] = []
    let channel: ReturnType<typeof supabase.channel> | undefined
    let dbTimer: number | undefined

    // DB(닉별 최고점, 내 닉 제외) + 라이브(내 닉 제외) 병합 → 내 라이브 엔트리는 항상 포함
    const renderMerged = () => {
      if (!ladder) return
      const byNick = new Map<string, Player>()
      for (const r of dbRows) {
        if (r.nickname === myNick) continue
        const cur = byNick.get(r.nickname)
        if (!cur || r.score > cur.score) byNick.set(r.nickname, { id: `db:${r.nickname}`, nickname: r.nickname, score: r.score })
      }
      let me: Player | undefined
      for (const p of livePlayers) {
        if (p.id === myId) { me = p; continue }
        if (p.nickname === myNick) continue
        const cur = byNick.get(p.nickname)
        if (!cur || p.score > cur.score) byNick.set(p.nickname, p)
      }
      const merged = [...byNick.values()]
      merged.push(me ?? { id: myId, nickname: myNick, score: currentScore })
      ladder.setPlayers(merged, myId)
    }

    const refreshDb = () => {
      void topScores(1000, sinceFor('weekly'))   // 주간 전체(최근 7일) — 한도 크게 잡아 내 등수가 정확히 계산되게
        .then(rows => { dbRows = rows.map(r => ({ nickname: r.nickname, score: r.score })); renderMerged() })
        .catch(() => {})
    }

    // presence 채널: 내 라이브 점수를 track, 모두의 상태를 sync로 수신
    const trackNow = () => { void channel?.track({ id: myId, nickname: myNick, score: currentScore }) }
    if (ladder) {
      channel = supabase.channel('live_arena', { config: { presence: { key: myId } } })
      channel
        .on('presence', { event: 'sync' }, () => {
          const state = channel!.presenceState() as Record<string, Array<{ id?: string; nickname?: string; score?: number }>>
          livePlayers = Object.values(state)
            .map(metas => metas[metas.length - 1])
            .filter(m => m && typeof m.score === 'number' && m.id)
            .map(m => ({ id: m.id as string, nickname: (m.nickname || '플레이어'), score: m.score as number }))
          renderMerged()
        })
        .subscribe((status) => { if (status === 'SUBSCRIBED') trackNow() })
      refreshDb()                                   // 시작 시 DB 점수 즉시 반영
      dbTimer = window.setInterval(refreshDb, 20000) // 20초마다 새 기록 갱신
    }

    g.events.on('state', (s: any) => {
      scoreText.setText(`${s.score}`)
      stageText.setText(`STAGE ${s.stage}`)
      if (s.stage !== barStage) { barStage = s.stage; barMax = s.timeRemaining }
      else barMax = Math.max(barMax, s.timeRemaining)
      const ratio = Phaser.Math.Clamp(s.timeRemaining / barMax, 0, 1)
      timeBar.width = width * 0.84 * ratio
      timeBar.fillColor = s.timeRemaining <= 10 ? 0xff5b5b : 0x6be36b
      useGameStore.getState().set({ score: s.score, stage: s.stage, timeRemaining: s.timeRemaining })

      currentScore = s.score
      if (channel) { // 점수 변동을 살짝 모아 라이브로 브로드캐스트
        if (trackTimer) clearTimeout(trackTimer)
        trackTimer = window.setTimeout(trackNow, 250)
      }
    })

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (trackTimer) clearTimeout(trackTimer)
      if (dbTimer) clearInterval(dbTimer)
      if (channel) supabase.removeChannel(channel)
    })
  }

  /** 3행 표시(위1·나·아래1) + 7행 버퍼, 1~4위 메달, 순위 변동 시 슬롯 롤링. */
  private buildRankLadder(width: number, height: number) {
    const rowH = width * 0.062
    const visRows = 3
    const panelW = width * 0.76
    const cx = width / 2
    const left = cx - panelW / 2
    const winH = visRows * rowH
    const panelTop = height - width * 0.06 - winH
    const centerY = panelTop + winH / 2
    const meIdx = 3
    const baseY = centerY - (meIdx * rowH + rowH / 2)
    const fs = `${Math.round(width * 0.034)}px`

    const bg = this.add.graphics().setDepth(38)
    bg.fillStyle(0x140d2e, 0.5); bg.fillRoundedRect(left, panelTop, panelW, winH, rowH * 0.3)
    bg.lineStyle(Math.max(2, width * 0.005), 0xffffff, 0.22); bg.strokeRoundedRect(left, panelTop, panelW, winH, rowH * 0.3)
    const title = this.add.text(cx, panelTop - rowH * 0.42, '실시간 랭킹', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.03)}px`, color: '#ffffff', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(40)

    const cont = this.add.container(0, baseY).setDepth(39)
    const rows: { bg: Phaser.GameObjects.Graphics; rank: Phaser.GameObjects.Text; medal: Phaser.GameObjects.Image; nick: Phaser.GameObjects.Text; score: Phaser.GameObjects.Text }[] = []
    for (let i = 0; i < 7; i++) {
      const y = i * rowH + rowH / 2
      const rbg = this.add.graphics()
      const rank = this.add.text(left + rowH * 0.75, y, '', { fontFamily: 'Arial', fontSize: fs, color: '#cdbfff', fontStyle: 'bold' }).setOrigin(0.5, 0.5)
      const medal = this.add.image(left + rowH * 0.75, y, 'crown').setDisplaySize(rowH * 0.78, rowH * 0.78).setVisible(false)
      const nick = this.add.text(left + rowH * 1.7, y, '', { fontFamily: 'Arial', fontSize: fs, color: '#ffffff' }).setOrigin(0, 0.5)
      const score = this.add.text(left + panelW - rowH * 0.45, y, '', { fontFamily: 'Arial', fontSize: fs, color: '#ffe066', fontStyle: 'bold' }).setOrigin(1, 0.5)
      cont.add([rbg, rank, medal, nick, score]); rows.push({ bg: rbg, rank, medal, nick, score })
    }
    const mshape = this.make.graphics({})
    mshape.fillStyle(0xffffff); mshape.fillRect(left, panelTop, panelW, winH)
    cont.setMask(mshape.createGeometryMask())

    const arrow = this.add.text(left + panelW + rowH * 0.1, centerY, '▲', {
      fontFamily: 'Arial', fontSize: `${Math.round(width * 0.05)}px`, color: '#6be36b', fontStyle: 'bold',
    }).setOrigin(0.5).setAlpha(0).setDepth(41)

    bg.setAlpha(0); cont.setAlpha(0); title.setAlpha(0)

    const maxNick = panelW - rowH * 1.7 - rowH * 1.4
    const fitNick = (t: Phaser.GameObjects.Text) => { t.setScale(1); if (t.width > maxNick) t.setScale(maxNick / t.width) }

    const render = (entries: Entry[]) => {
      for (let i = 0; i < 7; i++) {
        const e = entries[i]; const r = rows[i]; const y = i * rowH + rowH / 2
        r.bg.clear()
        if (!e) { r.rank.setText(''); r.medal.setVisible(false); r.nick.setText(''); r.score.setText(''); continue }
        if (e.isMe) {
          r.bg.fillStyle(0xffd23f, 0.95)
          r.bg.fillRoundedRect(left + rowH * 0.18, y - rowH / 2 + rowH * 0.08, panelW - rowH * 0.36, rowH - rowH * 0.16, rowH * 0.26)
        }
        const medalKey = e.rank === 1 ? 'crown' : e.rank === 2 ? 'medal_gold' : e.rank === 3 ? 'medal_silver' : e.rank === 4 ? 'medal_bronze' : null
        if (medalKey) {
          r.medal.setTexture(medalKey).setDisplaySize(rowH * 0.82, rowH * 0.82).setVisible(true)
          r.rank.setText('')
        } else {
          r.medal.setVisible(false)
          r.rank.setText(`${e.rank}`).setColor(e.isMe ? '#5a3f12' : '#cdbfff')
        }
        r.nick.setText(e.isMe ? `${e.nickname} (나)` : e.nickname).setColor(e.isMe ? '#3a2a6a' : '#ffffff')
        fitNick(r.nick)
        r.score.setText(`${e.score}`).setColor(e.isMe ? '#7a4a00' : '#ffe066')
      }
    }

    let lastRank = Number.POSITIVE_INFINITY
    let lastTop = -1
    return {
      setPlayers: (players: Player[], myId: string) => {
        if (!this.scene.isActive()) return
        const sorted = [...players].sort((a, b) => b.score - a.score || (a.id < b.id ? -1 : 1))
        const N = sorted.length
        const myIdx = sorted.findIndex(p => p.id === myId)
        if (myIdx < 0) return
        const rank = myIdx + 1
        // 3행 창 최상단 등수: 기본은 내 위 1칸, 경계(1등/꼴찌)에선 창이 꽉 차도록 클램프
        const topRank = Math.max(1, Math.min(rank - 1, Math.max(1, N - 2)))
        const entries: Entry[] = new Array(7).fill(null)
        for (let i = 0; i < 7; i++) {
          const r = topRank + (i - 2) // strip index i의 등수(index2 = 창 최상단 행)
          if (r >= 1 && r <= N) {
            const p = sorted[r - 1]
            entries[i] = { rank: r, nickname: p.nickname, score: p.score, isMe: p.id === myId }
          }
        }
        render(entries)
        bg.setAlpha(1); title.setAlpha(1); cont.setAlpha(1)

        // 슬라이드는 창 스크롤량(topRank 변화), 화살표는 내 등수 변화 기준
        const dTop = lastTop - topRank
        const dRank = lastRank - rank
        this.tweens.killTweensOf(cont)
        if (lastTop !== -1 && dTop !== 0) {
          const k = Math.min(Math.abs(dTop), 2)
          cont.y = baseY + (dTop > 0 ? k : -k) * rowH
          this.tweens.add({ targets: cont, y: baseY, duration: 900, ease: 'Quart.easeOut' })
        } else {
          cont.y = baseY
        }
        if (lastRank !== Number.POSITIVE_INFINITY && dRank !== 0) {
          this.tweens.killTweensOf(arrow)
          if (dRank > 0) {
            arrow.setText('▲').setColor('#6be36b').setAlpha(1).setY(centerY + rowH * 0.4)
            this.tweens.add({ targets: arrow, y: centerY - rowH * 1.0, alpha: 0, duration: 1000, ease: 'Cubic.easeOut' })
          } else {
            arrow.setText('▼').setColor('#ff6b6b').setAlpha(1).setY(centerY - rowH * 0.4)
            this.tweens.add({ targets: arrow, y: centerY + rowH * 1.0, alpha: 0, duration: 1000, ease: 'Cubic.easeOut' })
          }
        }
        lastTop = topRank
        lastRank = rank
      },
    }
  }
}
